/**
 * Welcome email-capture popup runtime.
 *
 * Pairs with `sections/email-capture-popup.liquid`. The section markup
 * is rendered hidden at first paint (style="display:none") to avoid
 * flash; this script decides whether + when to show it based on:
 *
 *   - window.YukiEmailPopupConfig (set by the section's inline script)
 *       .delaySeconds
 *       .dismissCooldownDays
 *       .endpointUrl  → '/apps/api/popup/subscribe' (App Proxy)
 *
 *   - localStorage `yuki:popup:state` JSON:
 *       { subscribedAt?: ISO string, lastDismissAt?: ISO string, lastShownAt?: ISO string }
 *
 * Decision tree on DOMContentLoaded:
 *   1. If subscribedAt is set                          → never show
 *   2. If lastDismissAt is within cooldownDays         → don't show
 *   3. If lastShownAt is within cooldownDays           → don't show
 *      (lastShownAt is stamped the moment the popup auto-shows, so a
 *       browser-Back / reload that re-runs this script does NOT relaunch
 *       it — the popup shows once per cooldown, dismissed or not)
 *   4. Otherwise → schedule show after delaySeconds
 *
 * Submission flow:
 *   - Prevent native form submit (we POST as JSON to the worker)
 *   - Disable submit button + show "Sending…" while in flight
 *   - On 200: flip card to success state, save subscribedAt
 *   - On 4xx/5xx: show inline error, re-enable button (user can retry)
 *
 * Dismiss flow:
 *   - × button click / backdrop click / ESC → hide + save lastDismissAt
 *
 * Defensive: every localStorage access wraps in try/catch so the
 * popup still works in private-browsing mode (it'll just always show
 * because we can't remember dismiss/subscribe state — acceptable
 * fallback, mirrors the wishlist-session.js convention).
 */
(function () {
  if (window.__yukiEmailPopupInit) return;
  window.__yukiEmailPopupInit = true;

  const STORAGE_KEY = 'yuki:popup:state';
  const cfg = window.YukiEmailPopupConfig || {};
  const delaySeconds = Math.max(0, Number(cfg.delaySeconds) || 7);
  const cooldownDays = Math.max(1, Number(cfg.dismissCooldownDays) || 30);
  const endpointUrl = String(cfg.endpointUrl || '/apps/api/popup/subscribe');

  function readState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_e) {
      return {};
    }
  }

  function writeState(patch) {
    try {
      const current = readState();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...patch }));
    } catch (_e) {
      // Private mode / quota exceeded — fall through; next page load
      // will treat the user as fresh. Acceptable; they'll dismiss again.
    }
  }

  function withinCooldown(iso) {
    if (!iso) return false;
    const ts = Date.parse(iso);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < cooldownDays * 24 * 60 * 60 * 1000;
  }

  function shouldShow() {
    const state = readState();
    if (state.subscribedAt) return false;                  // subscribed → never
    if (withinCooldown(state.lastDismissAt)) return false; // dismissed recently
    if (withinCooldown(state.lastShownAt)) return false;   // already shown recently
    return true;
  }

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function init() {
    const popup = qs('[data-yuki-email-popup]');
    const backdrop = qs('[data-yuki-email-popup-backdrop]');
    if (!popup || !backdrop) return; // section not on this page (feature disabled)

    const form = qs('[data-yuki-email-popup-form]', popup);
    const submitBtn = qs('[data-yuki-email-popup-submit]', popup);
    const submitLabel = qs('[data-yuki-submit-label]', popup);
    const submitSpinner = qs('[data-yuki-submit-spinner]', popup);
    const errorEl = qs('[data-yuki-email-popup-error]', popup);
    const closeButtons = popup.querySelectorAll('[data-yuki-email-popup-close]');

    function show() {
      popup.style.display = 'block';
      backdrop.style.display = 'block';
      popup.setAttribute('aria-hidden', 'false');
      backdrop.setAttribute('aria-hidden', 'false');
      // Next frame → toggle .is-open for CSS transition
      requestAnimationFrame(() => {
        popup.classList.add('is-open');
        backdrop.classList.add('is-open');
        // Autofocus the email input for keyboard users
        const input = qs('input[type="email"]', popup);
        if (input) {
          try { input.focus({ preventScroll: true }); } catch (_e) { input.focus(); }
        }
      });
    }

    function hide(saveDismiss) {
      popup.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      // Wait for CSS transition before display:none so the fade-out is visible
      setTimeout(() => {
        popup.style.display = 'none';
        backdrop.style.display = 'none';
        popup.setAttribute('aria-hidden', 'true');
        backdrop.setAttribute('aria-hidden', 'true');
      }, 280);
      if (saveDismiss && !popup.classList.contains('is-submitted')) {
        writeState({ lastDismissAt: new Date().toISOString() });
      }
    }

    function setError(message) {
      if (!errorEl) return;
      if (message) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      } else {
        errorEl.textContent = '';
        errorEl.hidden = true;
      }
    }

    function setBusy(isBusy) {
      if (!submitBtn) return;
      submitBtn.disabled = isBusy;
      if (submitLabel) submitLabel.hidden = isBusy;
      if (submitSpinner) submitSpinner.hidden = !isBusy;
    }

    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        setError('');
        const fd = new FormData(form);
        const email = String(fd.get('email') || '').trim().toLowerCase();
        // Light client-side validation — server re-validates anyway.
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          setError('Please enter a valid email address.');
          return;
        }
        setBusy(true);
        try {
          const res = await fetch(endpointUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ email, opt_in: true }),
          });
          if (!res.ok) {
            // Don't leak server detail — generic retry message.
            setError("That didn't work — please try again in a moment.");
            setBusy(false);
            return;
          }
          // Success: flip to success state + remember so we never show again.
          writeState({ subscribedAt: new Date().toISOString() });
          popup.classList.add('is-submitted');
          setBusy(false);
        } catch (_err) {
          setError('Network error — please check your connection and try again.');
          setBusy(false);
        }
      });
    }

    closeButtons.forEach((btn) => {
      btn.addEventListener('click', () => hide(true));
    });

    backdrop.addEventListener('click', () => hide(true));

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (popup.style.display === 'none') return;
      hide(true);
    });

    // Imperative trigger for other UI (e.g. the PDP "Get Your Code" offer
    // button). An explicit click always opens the popup, bypassing the
    // auto-show frequency rules. If the visitor already subscribed, they
    // see the success state (their code is already on its way).
    window.YukiEmailPopup = {
      open: function () { show(); },
      close: function () { hide(false); },
    };

    if (shouldShow()) {
      // Stamp lastShownAt the instant the popup fires — NOT only on dismiss.
      // Otherwise a visitor who leaves without closing it (e.g. browser Back,
      // which reloads + re-runs this script) re-arms the 7s timer and the
      // popup relaunches. Recording on show makes it once-per-cooldown.
      setTimeout(() => {
        writeState({ lastShownAt: new Date().toISOString() });
        show();
      }, delaySeconds * 1000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
